import { Lightning } from '@lightningjs/sdk'
import { getColor, grey50, grey960, transparent } from '../../constants/colors'
import { Images } from '../../constants/paths'
import { PlayPauseButton } from './playerButton'

const BORDER_WIDTH = 10
const MID_BORDER_WIDTH = 6
const BORDER_RADIUS = 28
const MARGIN = 12

export class ThumbnailOverlay extends Lightning.Component {
  private _numRowSprites = 0
  private _numColSprites = 0
  private _spriteSheetDuration = 0
  private _spriteWidth = 0
  private _spriteHeight = 0
  private _spriteDuration = 0
  private _trickplayInfo: TrickplayInfo = null

  static _template() {
    return {
      collision: true,

      Border: {
        w: (w) => w + BORDER_WIDTH * 2,
        h: (h) => h + BORDER_WIDTH * 2,
        x: -BORDER_WIDTH - MARGIN,
        y: -BORDER_WIDTH,
        rect: true,
        zIndex: 1,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: BORDER_RADIUS,
          stroke: 8,
          fillColor: transparent,
          strokeColor: getColor(grey50),
        },
      },
      MidBorder: {
        w: (w) => w + MID_BORDER_WIDTH * 2,
        h: (h) => h + MID_BORDER_WIDTH * 2,
        x: -MID_BORDER_WIDTH - MARGIN,
        y: -MID_BORDER_WIDTH,
        rect: true,
        zIndex: 1,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          stroke: 6,
          strokeColor: getColor(grey960),
          fillColor: transparent,
          radius: BORDER_RADIUS,
        },
      },
      Thumbnail: {
        x: -MARGIN,
        w: 320,
        h: 180,
        shader: null,
      },
      PlayPauseButton: {
        visible: false,
        mountX: 0.5,
        mountY: 0.5,
        type: PlayPauseButton,
        images: {
          play: Images.PLAYER_PLAY,
        },
      },
    }
  }

  get thumbnail() {
    return this.tag('Thumbnail')
  }

  get isTrickplayPreloaded() {
    return this._trickplayInfo !== null
  }

  get playPauseButton() {
    return this.tag('PlayPauseButton') as PlayPauseButton
  }

  get trickplayInfo() {
    return this._trickplayInfo
  }

  _init() {
    this.playPauseButton._unfocus()
    this.playPauseButton.x = this.thumbnail.w / 2 - MARGIN
    this.playPauseButton.y =
      this.thumbnail.h / 2 - (this.playPauseButton.h - this.playPauseButton.w) / 2
  }

  showThumbnail(url: string) {
    this.patch({ Thumbnail: { src: url } })
  }

  setTrickplayInfo(info: TrickplayInfo) {
    if (!info?.thumbnails?.length) {
      return
    }

    const ss = info.thumbnails[0]
    this._spriteSheetDuration = info.targetDuration
    const layout = ss.layout?.split('x')
    this._numRowSprites = Number(layout[0])
    this._numColSprites = Number(layout[1])
    const res = ss.resolution?.split('x')
    this._spriteWidth = Number(res[0])
    this._spriteHeight = Number(res[1])
    this._spriteDuration = ss.duration

    this._trickplayInfo = info
  }

  updateThumbnail(curTime: number) {
    if (!this._trickplayInfo || curTime < 0) return

    const rTime = Math.round(curTime)
    const ssIndex = Math.floor(rTime / this._spriteSheetDuration)
    const spriteTime = Math.floor(rTime % this._spriteSheetDuration)
    const spriteIndex = Math.floor(spriteTime / this._spriteDuration)

    const ss = this._trickplayInfo.thumbnails[ssIndex]
    if (!ss) return

    let url = `${this.adjustUrl(this._trickplayInfo.baseUrl, ss.imageURL)}`

    if (ss.imageURL.startsWith('../')) {
      url += `${ss.imageURL.replace('../', '/')}`
    } else if (ss.imageURL.startsWith('/')) {
      url += `${ss.imageURL}`
    } else {
      url += `/${ss.imageURL}`
    }

    this.thumbnail.patch({
      texture: Lightning.Tools.getSvgTexture(
        url,
        this._spriteWidth * this._numColSprites,
        this._spriteHeight * this._numRowSprites,
      ),
    })

    const xPos = (spriteIndex % this._numColSprites) * this._spriteWidth
    const yPos = Math.floor(spriteIndex / this._numRowSprites) * this._spriteHeight

    this.thumbnail.texture.enableClipping(xPos, yPos, this._spriteWidth, this._spriteHeight)
  }

  adjustUrl(baseUrl, secondUrl) {
    const url = new URL(baseUrl)
    let pathSegments = url.pathname.split('/')

    if (secondUrl.startsWith('../')) {
      pathSegments = pathSegments.slice(0, -2) // Remove last two segments (page + one folder)
    } else {
      pathSegments = pathSegments.slice(0, -1) // Remove only the page
    }

    const cleanPath = pathSegments.join('/') || '/'
    return `${url.origin}${cleanPath}`
  }

  _handleHover() {
    this.playPauseButton._focus()
  }

  _handleUnhover() {
    this.playPauseButton._unfocus()
  }

  _handleClick() {
    return false
  }
}
